import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useSupabaseTable(table, { select = '*', orderBy = 'created_at', ascending = false } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: rows, error: err } = await supabase
      .from(table)
      .select(select)
      .order(orderBy, { ascending });
    if (err) {
      setError(err);
      setData([]);
    } else {
      setData(rows || []);
    }
    setLoading(false);
  }, [table, select, orderBy, ascending]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const create = useCallback(async (payload) => {
    const { data: row, error: err } = await supabase
      .from(table)
      .insert(payload)
      .select()
      .single();
    if (err) throw err;
    setData(prev => [row, ...prev]);
    return row;
  }, [table]);

  const update = useCallback(async (id, payload) => {
    const { data: row, error: err } = await supabase
      .from(table)
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    setData(prev => prev.map(x => (x.id === id ? row : x)));
    return row;
  }, [table]);

  const remove = useCallback(async (id) => {
    const { error: err } = await supabase.from(table).delete().eq('id', id);
    if (err) throw err;
    setData(prev => prev.filter(x => x.id !== id));
  }, [table]);

  return { data, loading, error, create, update, remove, refetch };
}
